import useHasActiveFilters from '../../../../hooks/useHasActiveFilters';
import { CATALOG_FILTERS_KEYS } from '../catalogFilters.constants';
import ProductCollectionListV2 from './ProductCollectionListV2';
import CatalogProductsEmptyState from './components/CatalogProductsEmptyState';
import CatalogScrollSeekPlaceholder from './components/CatalogScrollSeekPlaceholder';
import CatalogSectionEmptyDropZone from './components/CatalogSectionEmptyDropZone';
import CatalogSectionEmptyPlaceholder from './components/CatalogSectionEmptyPlaceholder';
import CatalogSectionHeaderV2 from './components/CatalogSectionHeaderV2';
import useCatalogProductsListV2View from './hooks/useCatalogProductsListV2View';
import { buildSectionDroppableId } from './utils/catalogDragAndDrop.utils';

interface CatalogProductsListV2Props {
  catalogId: string;
  maxItemsPerRow: number;
  pageSize: number;
  isOfflineMode: boolean;
  canPersist: boolean;
  canManageSections: boolean;
}

const CatalogProductsListV2 = (props: CatalogProductsListV2Props) => {
  const {
    list,
    virtuoso,
    renameSection,
    scrollParent,
    dragAndDrop,
    restoreStateFrom,
    handleRangeChanged,
    handleIsScrolling,
    scrollSeekConfiguration,
  } = useCatalogProductsListV2View(props);

  const sections = list.status === 'ready' ? list.sections : [];

  const hasActiveFilters = useHasActiveFilters({
    filterKeys: Object.values(CATALOG_FILTERS_KEYS),
    ignoredKeys: ['tab'],
  });

  return (
    <ProductCollectionListV2
      catalogId={props.catalogId}
      list={list}
      virtuoso={virtuoso}
      scrollParent={scrollParent}
      restoreStateFrom={restoreStateFrom}
      dragAndDrop={dragAndDrop}
      scrollSeekConfiguration={scrollSeekConfiguration}
      scrollSeekPlaceholder={CatalogScrollSeekPlaceholder}
      maxItemsPerRow={props.maxItemsPerRow}
      emptyState={<CatalogProductsEmptyState hasActiveFilters={hasActiveFilters} />}
      onRangeChanged={handleRangeChanged}
      onIsScrolling={handleIsScrolling}
      groupContent={(groupIndex) => {
        const section = sections.at(groupIndex);

        if (!section) {
          return null;
        }

        return (
          <>
            <CatalogSectionHeaderV2
              sectionName={section.catalogSectionName}
              groupIndex={groupIndex}
              onRename={
                props.canManageSections && !section.isDefault
                  ? (name) => renameSection({ sectionId: section.catalogSectionId, name })
                  : undefined
              }
            />

            {section.itemsCount === 0 &&
              (dragAndDrop.isEnabled ? (
                <CatalogSectionEmptyDropZone
                  sectionId={section.catalogSectionId}
                  isDropTarget={
                    dragAndDrop.dropIndicator?.overId ===
                    buildSectionDroppableId(section.catalogSectionId)
                  }
                />
              ) : (
                <CatalogSectionEmptyPlaceholder />
              ))}
          </>
        );
      }}
    />
  );
};

export default CatalogProductsListV2;
